import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Activity,
  AlertTriangle,
  Calendar,
  ChevronDown,
  Clock,
  CloudRain,
  Droplets,
  MapPin,
  Mountain,
  Satellite,
  TrendingDown,
  Zap,
} from 'lucide-react'
import {
  AreaChart,
  Area,
  Bar,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
} from 'recharts'
import { cn } from '@/lib/utils'
import {
  RETROCAST_SCENARIOS,
  EW_LEVEL_COLORS,
  EW_LEVEL_LABELS,
  type RetrocastScenario,
  type EWLevel,
} from '@/lib/retrocastData'

// ----------------------------------------------------------
// Scenario selector
// ----------------------------------------------------------

function ScenarioSelector({
  scenarios,
  selected,
  onSelect,
}: {
  scenarios: RetrocastScenario[]
  selected: RetrocastScenario
  onSelect: (s: RetrocastScenario) => void
}) {
  const [open, setOpen] = useState(false)

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-3 px-4 py-2.5 glass-strong rounded-xl border border-terra-blue/20 hover:border-terra-blue/40 transition-all min-w-[280px]"
      >
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{
            background: selected.eventType === 'landslide' ? '#f9731618' : '#0ea5e918',
            border: `1px solid ${selected.eventType === 'landslide' ? '#f9731635' : '#0ea5e935'}`,
          }}
        >
          {selected.eventType === 'landslide' ? (
            <Mountain size={16} className="text-terra-orange" />
          ) : (
            <Droplets size={16} className="text-terra-blue" />
          )}
        </div>
        <div className="text-left flex-1">
          <div className="text-sm font-bold text-white">{selected.name}</div>
          <div className="text-xs text-slate-400">{selected.subtitle}</div>
        </div>
        <ChevronDown
          size={14}
          className={cn('text-slate-500 transition-transform', open && 'rotate-180')}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="absolute top-full mt-2 left-0 right-0 z-50 glass-strong rounded-xl p-2 shadow-2xl"
          >
            {scenarios.map((s) => (
              <button
                key={s.id}
                onClick={() => {
                  onSelect(s)
                  setOpen(false)
                }}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all',
                  s.id === selected.id
                    ? 'bg-terra-blue/10 border border-terra-blue/20'
                    : 'hover:bg-slate-800/60',
                )}
              >
                {s.eventType === 'landslide' ? (
                  <Mountain size={14} className="text-terra-orange" />
                ) : (
                  <Droplets size={14} className="text-terra-blue" />
                )}
                <div className="text-left">
                  <div className="text-xs font-semibold text-white">{s.name}</div>
                  <div className="text-xs text-slate-500">{s.subtitle}</div>
                </div>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ----------------------------------------------------------
// Key metrics row
// ----------------------------------------------------------

function MetricCard({
  icon: Icon,
  label,
  value,
  sub,
  color,
  delay,
}: {
  icon: React.ComponentType<{ size?: number | string; className?: string }>
  label: string
  value: string
  sub?: string
  color: string
  delay: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.3 }}
      className="glass-strong rounded-xl p-4"
    >
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center"
          style={{ background: color + '18', border: `1px solid ${color}35` }}
        >
          <span style={{ color }}><Icon size={14} /></span>
        </div>
        <span className="text-xs text-slate-500">{label}</span>
      </div>
      <div className="text-xl font-bold text-white">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </motion.div>
  )
}

// ----------------------------------------------------------
// EW Timeline chart (hero chart)
// ----------------------------------------------------------

function EWTimelineChart({ scenario }: { scenario: RetrocastScenario }) {
  const data = scenario.ewTimeline.map((p) => ({
    date: p.date.slice(5), // MM-DD
    fullDate: p.date,
    ew: p.ew,
    level: p.level,
    color: EW_LEVEL_COLORS[p.level],
  }))

  const eventIdx = data.findIndex((d) => d.fullDate === scenario.eventDate)

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="terra-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Zap size={14} className="text-terra-yellow" />
          <h3 className="text-sm font-semibold text-white">Score Early Warning nel Tempo</h3>
        </div>
        <div className="flex items-center gap-3">
          {(['GREEN', 'YELLOW', 'ORANGE', 'RED'] as EWLevel[]).map((l) => (
            <div key={l} className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full" style={{ background: EW_LEVEL_COLORS[l] }} />
              <span className="text-xs text-slate-500">{EW_LEVEL_LABELS[l]}</span>
            </div>
          ))}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={data} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
          <defs>
            <linearGradient id="ewGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.3)" />
          {/* EW level background zones */}
          <ReferenceArea y1={0} y2={5} fill="#10b981" fillOpacity={0.05} />
          <ReferenceArea y1={5} y2={7} fill="#f59e0b" fillOpacity={0.05} />
          <ReferenceArea y1={7} y2={8.5} fill="#f97316" fillOpacity={0.08} />
          <ReferenceArea y1={8.5} y2={10} fill="#ef4444" fillOpacity={0.1} />
          {/* Threshold lines */}
          <ReferenceLine y={5} stroke="#f59e0b" strokeDasharray="4 4" strokeOpacity={0.5} />
          <ReferenceLine y={7} stroke="#f97316" strokeDasharray="4 4" strokeOpacity={0.5} />
          <ReferenceLine y={8.5} stroke="#ef4444" strokeDasharray="4 4" strokeOpacity={0.5} />
          {/* Event date line */}
          {eventIdx >= 0 && (
            <ReferenceLine
              x={data[eventIdx]?.date}
              stroke="#ef4444"
              strokeWidth={2}
              label={{
                value: 'EVENTO',
                fill: '#ef4444',
                fontSize: 10,
                fontWeight: 700,
                position: 'top',
              }}
            />
          )}
          <XAxis
            dataKey="date"
            tick={{ fill: '#64748b', fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            interval={Math.floor(data.length / 8)}
          />
          <YAxis
            domain={[0, 10]}
            tick={{ fill: '#64748b', fontSize: 10 }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            contentStyle={{
              background: 'rgba(15,23,42,0.95)',
              border: '1px solid rgba(14,165,233,0.2)',
              borderRadius: '8px',
              fontSize: '12px',
              color: '#e2e8f0',
            }}
            formatter={(val: number) => [`${val.toFixed(2)}`, 'EW Score']}
            labelFormatter={(label: string) => `Data: ${label}`}
          />
          <Area
            type="monotone"
            dataKey="ew"
            stroke="#ef4444"
            fill="url(#ewGrad)"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, fill: '#ef4444', stroke: '#fff', strokeWidth: 1 }}
          />
          <Line
            type="monotone"
            dataKey="ew"
            stroke="none"
            dot={(props: { cx: number; cy: number; payload: { color: string }; index: number }) => {
              const { cx, cy, payload, index } = props
              if (index % 3 !== 0) return <circle key={index} cx={0} cy={0} r={0} />
              return (
                <circle
                  key={index}
                  cx={cx}
                  cy={cy}
                  r={3}
                  fill={payload.color}
                  stroke={payload.color}
                  strokeOpacity={0.3}
                  strokeWidth={4}
                />
              )
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </motion.div>
  )
}

// ----------------------------------------------------------
// Rainfall chart
// ----------------------------------------------------------

function RainfallChart({ scenario }: { scenario: RetrocastScenario }) {
  const data = scenario.rainfallData.map((p) => ({
    date: p.date.slice(5),
    fullDate: p.date,
    rain: p.rainfall_mm,
    cumulative: p.cumulative_mm,
  }))

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.25 }}
      className="terra-card"
    >
      <div className="flex items-center gap-2 mb-4">
        <CloudRain size={14} className="text-terra-blue" />
        <h3 className="text-sm font-semibold text-white">Precipitazioni</h3>
        <span className="text-xs text-slate-500 ml-auto">
          Totale: {scenario.totalRainfallMm.toLocaleString('it-IT')} mm
        </span>
      </div>

      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={data} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.3)" />
          <XAxis
            dataKey="date"
            tick={{ fill: '#64748b', fontSize: 9 }}
            tickLine={false}
            axisLine={false}
            interval={Math.floor(data.length / 6)}
          />
          <YAxis
            yAxisId="rain"
            tick={{ fill: '#64748b', fontSize: 9 }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            yAxisId="cum"
            orientation="right"
            tick={{ fill: '#64748b', fontSize: 9 }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            contentStyle={{
              background: 'rgba(15,23,42,0.95)',
              border: '1px solid rgba(14,165,233,0.2)',
              borderRadius: '8px',
              fontSize: '11px',
              color: '#e2e8f0',
            }}
            formatter={(val: number, name: string) => [
              `${val.toFixed(1)} mm`,
              name === 'rain' ? 'Giornaliera' : 'Cumulata',
            ]}
          />
          <Bar yAxisId="rain" dataKey="rain" fill="#0ea5e9" fillOpacity={0.6} radius={[2, 2, 0, 0]} />
          <Line
            yAxisId="cum"
            type="monotone"
            dataKey="cumulative"
            stroke="#06b6d4"
            strokeWidth={2}
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </motion.div>
  )
}

// ----------------------------------------------------------
// InSAR Displacement chart
// ----------------------------------------------------------

function InSARChart({ scenario }: { scenario: RetrocastScenario }) {
  const data = scenario.insarData.map((p) => ({
    date: p.date.slice(0, 7), // YYYY-MM
    displacement: p.displacement_mm,
    velocity: p.velocity_mm_yr,
  }))

  const hasAcceleration = scenario.insarData.some((p) => p.acceleration_mm_yr2 !== 0)

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="terra-card"
    >
      <div className="flex items-center gap-2 mb-4">
        <Satellite size={14} className="text-terra-purple" />
        <h3 className="text-sm font-semibold text-white">
          Spostamento InSAR (Sentinel-1)
        </h3>
        {hasAcceleration && (
          <span className="ml-auto flex items-center gap-1 text-xs text-terra-red">
            <TrendingDown size={11} />
            Accelerazione rilevata
          </span>
        )}
      </div>

      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={data} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.3)" />
          <XAxis
            dataKey="date"
            tick={{ fill: '#64748b', fontSize: 9 }}
            tickLine={false}
            axisLine={false}
            interval={Math.floor(data.length / 6)}
          />
          <YAxis
            yAxisId="disp"
            tick={{ fill: '#64748b', fontSize: 9 }}
            tickLine={false}
            axisLine={false}
            label={{
              value: 'mm',
              angle: -90,
              position: 'insideLeft',
              fill: '#64748b',
              fontSize: 10,
            }}
          />
          <YAxis
            yAxisId="vel"
            orientation="right"
            tick={{ fill: '#64748b', fontSize: 9 }}
            tickLine={false}
            axisLine={false}
            label={{
              value: 'mm/yr',
              angle: 90,
              position: 'insideRight',
              fill: '#64748b',
              fontSize: 10,
            }}
          />
          <Tooltip
            contentStyle={{
              background: 'rgba(15,23,42,0.95)',
              border: '1px solid rgba(139,92,246,0.2)',
              borderRadius: '8px',
              fontSize: '11px',
              color: '#e2e8f0',
            }}
            formatter={(val: number, name: string) => [
              `${val.toFixed(1)} ${name === 'displacement' ? 'mm' : 'mm/yr'}`,
              name === 'displacement' ? 'Spostamento' : 'Velocita',
            ]}
          />
          <Area
            yAxisId="disp"
            type="monotone"
            dataKey="displacement"
            stroke="#8b5cf6"
            fill="#8b5cf6"
            fillOpacity={0.15}
            strokeWidth={2}
            dot={{ r: 3, fill: '#8b5cf6' }}
          />
          <Line
            yAxisId="vel"
            type="monotone"
            dataKey="velocity"
            stroke="#ef4444"
            strokeWidth={1.5}
            strokeDasharray="5 3"
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {hasAcceleration && (
        <div className="mt-3 p-3 rounded-lg bg-terra-red/5 border border-terra-red/20">
          <div className="flex items-start gap-2">
            <AlertTriangle size={12} className="text-terra-red mt-0.5 flex-shrink-0" />
            <p className="text-xs text-slate-300">
              <strong className="text-terra-red">Accelerazione anomala</strong> rilevata da Nov 2025:
              velocita da -15 a <strong>{scenario.peakVelocityMmYr} mm/yr</strong>.
              Spostamento cumulato: <strong>{Math.abs(scenario.peakDisplacementMm).toFixed(1)} mm</strong>.
              Pattern tipico di fase pre-collasso.
            </p>
          </div>
        </div>
      )}
    </motion.div>
  )
}

// ----------------------------------------------------------
// SWI Soil Moisture chart
// ----------------------------------------------------------

function SWIChart({ scenario }: { scenario: RetrocastScenario }) {
  const data = scenario.ewTimeline.map((p) => ({
    date: p.date.slice(5),
    swi: p.swi,
  }))

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.35 }}
      className="terra-card"
    >
      <div className="flex items-center gap-2 mb-4">
        <Droplets size={14} className="text-terra-cyan" />
        <h3 className="text-sm font-semibold text-white">Soil Water Index (SWI)</h3>
        <span className="text-xs text-slate-500 ml-auto">Copernicus CGLS</span>
      </div>

      <ResponsiveContainer width="100%" height={160}>
        <AreaChart data={data} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
          <defs>
            <linearGradient id="swiGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.3)" />
          <XAxis
            dataKey="date"
            tick={{ fill: '#64748b', fontSize: 9 }}
            tickLine={false}
            axisLine={false}
            interval={Math.floor(data.length / 6)}
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fill: '#64748b', fontSize: 9 }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            contentStyle={{
              background: 'rgba(15,23,42,0.95)',
              border: '1px solid rgba(6,182,212,0.2)',
              borderRadius: '8px',
              fontSize: '11px',
              color: '#e2e8f0',
            }}
            formatter={(val: number) => [`${val.toFixed(1)}%`, 'SWI']}
          />
          <ReferenceLine y={80} stroke="#f97316" strokeDasharray="4 4" strokeOpacity={0.5} />
          <Area
            type="monotone"
            dataKey="swi"
            stroke="#06b6d4"
            fill="url(#swiGrad)"
            strokeWidth={2}
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>

      {scenario.id === 'emilia' && (
        <div className="mt-3 p-3 rounded-lg bg-terra-orange/5 border border-terra-orange/20">
          <div className="flex items-start gap-2">
            <Activity size={12} className="text-terra-orange mt-0.5 flex-shrink-0" />
            <p className="text-xs text-slate-300">
              <strong className="text-terra-orange">Paradosso siccita-saturazione</strong>:
              SWI dal 10° al 90° percentile in soli 10 giorni (2-16 maggio 2023).
              Suolo impermeabilizzato dalla siccita ha amplificato il runoff superficiale.
            </p>
          </div>
        </div>
      )}
    </motion.div>
  )
}

// ----------------------------------------------------------
// Event timeline
// ----------------------------------------------------------

function EventTimeline({ scenario }: { scenario: RetrocastScenario }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="terra-card"
    >
      <div className="flex items-center gap-2 mb-4">
        <Clock size={14} className="text-slate-400" />
        <h3 className="text-sm font-semibold text-white">Cronologia Eventi</h3>
      </div>

      <div className="space-y-0">
        {scenario.keyEvents.map((evt, i) => {
          const color =
            evt.type === 'critical'
              ? '#ef4444'
              : evt.type === 'warning'
              ? '#f97316'
              : '#64748b'

          return (
            <div key={i} className="flex gap-3">
              {/* Timeline line */}
              <div className="flex flex-col items-center">
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1"
                  style={{ background: color, boxShadow: `0 0 8px ${color}40` }}
                />
                {i < scenario.keyEvents.length - 1 && (
                  <div className="w-px flex-1 bg-slate-700/60 my-1" />
                )}
              </div>

              {/* Content */}
              <div className="pb-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 font-mono">{evt.date}</span>
                </div>
                <p
                  className={cn(
                    'text-xs mt-0.5',
                    evt.type === 'critical'
                      ? 'text-terra-red font-semibold'
                      : evt.type === 'warning'
                      ? 'text-terra-orange'
                      : 'text-slate-400',
                  )}
                >
                  {evt.label}
                </p>
              </div>
            </div>
          )
        })}
      </div>
    </motion.div>
  )
}

// ----------------------------------------------------------
// EW Components breakdown
// ----------------------------------------------------------

function EWComponentsPanel({ scenario }: { scenario: RetrocastScenario }) {
  const data = scenario.ewTimeline.map((p) => ({
    date: p.date.slice(5),
    'Pioggia': p.rainfall_mm,
    'SWI': p.swi,
  }))

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.35 }}
      className="terra-card"
    >
      <div className="flex items-center gap-2 mb-4">
        <Activity size={14} className="text-terra-green" />
        <h3 className="text-sm font-semibold text-white">Componenti EW</h3>
      </div>

      <div className="p-3 rounded-lg bg-slate-900/60 border border-slate-800/60 mb-4">
        <p className="text-xs text-slate-400 font-mono text-center">
          EW = 10 × (0.30×Susc + 0.30×InSAR + 0.20×Rain + 0.20×SWI)
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {[
          {
            label: 'Suscettibilita',
            value: scenario.ewTimeline[0]?.insar_accel !== undefined
              ? (scenario.id === 'niscemi' ? '0.882' : '0.862')
              : '—',
            weight: '30%',
            color: '#f97316',
            desc: 'Feature stack 14 bande, XGBoost',
          },
          {
            label: 'InSAR Accel.',
            value: scenario.id === 'niscemi' ? 'MAX' : 'Stabile',
            weight: '30%',
            color: '#8b5cf6',
            desc: scenario.id === 'niscemi'
              ? 'Accelerazione -1164 mm/yr²'
              : 'Velocita costante -4 mm/yr',
          },
          {
            label: 'Pioggia Cum.',
            value: `${scenario.totalRainfallMm} mm`,
            weight: '20%',
            color: '#0ea5e9',
            desc: `Max giornaliero: ${scenario.maxDailyRainfallMm} mm`,
          },
          {
            label: 'SWI Anomalia',
            value: scenario.id === 'niscemi' ? '+0.31' : '+0.67',
            weight: '20%',
            color: '#06b6d4',
            desc: scenario.id === 'niscemi'
              ? 'Saturazione progressiva'
              : 'Paradosso siccita-saturazione',
          },
        ].map((comp) => (
          <div
            key={comp.label}
            className="p-3 rounded-lg bg-slate-900/40 border border-slate-800/60"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-500">{comp.label}</span>
              <span className="text-xs font-mono" style={{ color: comp.color }}>
                w={comp.weight}
              </span>
            </div>
            <div className="text-sm font-bold text-white">{comp.value}</div>
            <div className="text-xs text-slate-600 mt-0.5">{comp.desc}</div>
          </div>
        ))}
      </div>

      {/* Pioggia + SWI overlay chart */}
      <div className="mt-4">
        <ResponsiveContainer width="100%" height={120}>
          <ComposedChart data={data} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.2)" />
            <XAxis
              dataKey="date"
              tick={{ fill: '#64748b', fontSize: 8 }}
              tickLine={false}
              axisLine={false}
              interval={Math.floor(data.length / 5)}
            />
            <YAxis tick={{ fill: '#64748b', fontSize: 8 }} tickLine={false} axisLine={false} />
            <Bar dataKey="Pioggia" fill="#0ea5e9" fillOpacity={0.4} radius={[1, 1, 0, 0]} />
            <Line type="monotone" dataKey="SWI" stroke="#06b6d4" strokeWidth={1.5} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  )
}

// ----------------------------------------------------------
// Page
// ----------------------------------------------------------

export default function RetrocastPage() {
  const [scenario, setScenario] = useState(RETROCAST_SCENARIOS[0])

  const eventColor = scenario.eventType === 'landslide' ? '#f97316' : '#0ea5e9'
  const maxLevelColor = scenario.firstRedDate ? EW_LEVEL_COLORS.RED : EW_LEVEL_COLORS.ORANGE

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-xl font-bold text-white">Retrocast & Validazione</h1>
            <p className="text-sm text-slate-400 mt-0.5">
              Analisi retrospettiva su eventi storici reali
            </p>
          </div>

          <ScenarioSelector
            scenarios={RETROCAST_SCENARIOS}
            selected={scenario}
            onSelect={setScenario}
          />
        </div>
      </motion.div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <MetricCard
          icon={Zap}
          label="Max EW Score"
          value={scenario.maxEW.toFixed(2)}
          sub={scenario.firstRedDate ? 'RED' : 'ORANGE'}
          color={maxLevelColor}
          delay={0.05}
        />
        <MetricCard
          icon={Clock}
          label="Lead Time"
          value={`${scenario.leadTimeDays} gg`}
          sub="Dal primo ORANGE"
          color="#f59e0b"
          delay={0.08}
        />
        <MetricCard
          icon={Calendar}
          label="Data Evento"
          value={scenario.eventDate.slice(5)}
          sub={scenario.eventDate.slice(0, 4)}
          color={eventColor}
          delay={0.11}
        />
        <MetricCard
          icon={CloudRain}
          label="Pioggia Totale"
          value={`${scenario.totalRainfallMm}`}
          sub="mm cumulati"
          color="#0ea5e9"
          delay={0.14}
        />
        <MetricCard
          icon={Satellite}
          label="Spostamento"
          value={`${Math.abs(scenario.peakDisplacementMm).toFixed(0)} mm`}
          sub={`Vel. ${Math.abs(scenario.peakVelocityMmYr).toFixed(0)} mm/yr`}
          color="#8b5cf6"
          delay={0.17}
        />
        <MetricCard
          icon={MapPin}
          label="Localita"
          value={scenario.name}
          sub={scenario.region}
          color={eventColor}
          delay={0.2}
        />
      </div>

      {/* Scenario insight banner */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className={cn(
          'rounded-xl p-4 border',
          scenario.eventType === 'landslide'
            ? 'bg-terra-orange/5 border-terra-orange/20'
            : 'bg-terra-blue/5 border-terra-blue/20',
        )}
      >
        <div className="flex items-start gap-3">
          <AlertTriangle size={16} style={{ color: eventColor }} className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-white mb-1">
              {scenario.eventType === 'landslide'
                ? 'Frana da innesco pluviometrico con precursore InSAR'
                : 'Alluvione da saturazione rapida post-siccita'}
            </p>
            <p className="text-xs text-slate-400">
              {scenario.id === 'niscemi'
                ? `Il sistema TERRA avrebbe generato un'allerta ORANGE il 14 dicembre 2025 — 32 giorni prima dell'evento del 15 gennaio 2026. L'accelerazione InSAR da -15 a -174 mm/yr e le piogge cumulate di 1.094 mm hanno portato lo score a 9.57 (RED). Tempo sufficiente per evacuazione e protezione civile.`
                : `L'alluvione dell'Emilia-Romagna del maggio 2023 ha colpito dopo mesi di siccita estrema. Il sistema TERRA avrebbe rilevato la prima anomalia il 2 maggio (YELLOW) e generato allerta ORANGE il 15 maggio — 1 giorno prima dell'evento. Il paradosso siccita-saturazione (SWI dal 10° al 90° percentile in 10 giorni) ha amplificato il rischio.`}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Hero EW chart */}
      <EWTimelineChart scenario={scenario} />

      {/* Grid: Rainfall + InSAR side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RainfallChart scenario={scenario} />
        <InSARChart scenario={scenario} />
      </div>

      {/* Grid: SWI + EW Components */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SWIChart scenario={scenario} />
        <EWComponentsPanel scenario={scenario} />
      </div>

      {/* Event timeline */}
      <EventTimeline scenario={scenario} />

      {/* Footer note */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="glass rounded-xl p-4"
      >
        <div className="flex items-start gap-3">
          <Mountain size={14} className="text-slate-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-slate-400">
            I retrocast sono validati su dataset di eventi storici reali. Niscemi: frana del 15 gennaio 2026
            (IFFI + dati locali). Emilia-Romagna: alluvione maggio 2023 (inventario RER2023: 80.997 poligoni
            frana su Zenodo, EMSN154 estensione alluvione). I dati meteo e InSAR sono simulazioni fedeli
            ai pattern osservati. La validazione su dati operativi reali e prevista nella Fase 2 del POC.
          </p>
        </div>
      </motion.div>
    </div>
  )
}
